import { qs, isSP, delayedCall, vw } from '../common/utils'
import { gsap } from 'gsap'
import { CustomEase } from 'gsap/CustomEase'

gsap.registerPlugin(CustomEase)

export default class Sec1_KV {
  constructor() {
    this.rootEl = qs('.p-top-kv')
    if(!this.rootEl) return

    this.home       = qs('.home')
    this.title      = qs('.p-top-kv__title')
    this.back_cross = qs('.p-top-kv__back__cross')

    this.stageEl = qs('.p-top-kv__front__stage', this.rootEl)
    if(!this.stageEl) return

    this.PIXI = window.PIXI || globalThis.PIXI
    if(!this.PIXI) return

    const { Application, Assets, Container, Sprite, Graphics } = this.PIXI
    this.Application = Application
    this.Assets = Assets
    this.Container = Container
    this.Sprite = Sprite
    this.Graphics = Graphics


    const { width, height } = this.getStageSize()

    this.stageW = width
    this.stageH = height

    // 1400 x 800 を基準としたスケール
    this.rootScale  = isSP() ? this.stageH / 800 : this.stageH / 800

    this.MASK_SKEW_X = -17.62 * Math.PI / 180

    // 一番小さいときのサイズ
    this.CONTAINER_INIT_SCALE = isSP() ? .75 : .75

    this.MASK_WIDTH  = isSP() ? 220 : 425 * .5
    this.MASK_HEIGHT = isSP() ? 400 * 1.05 : 800 * .5 * 1.05 // skewを使うと高さが縮むので少しだけ大きく

    this.CROSS_WIDTH  = isSP() ? 67.32 : 140 * .8
    this.CROSS_HEIGHT = isSP() ? 800 : 640

    if(this.isTabletPortrait())
    {
      this.CROSS_WIDTH  = 140 * .6
    }

    this.animationCount = 0

    this.app = null
    this.items = []
    this.isInViewport = true
    this.isPageVisible = typeof document === 'undefined' ? true : !document.hidden
    this.isRendering = false
    this.viewportObserver = null
    this.onVisibilityChange = this.handleVisibilityChange.bind(this)

    this.textureManifest = window.kvInfo

    this.DEVICE = isSP() ? "sp" : "pc"
    this.PATH   = this.textureManifest.imgPath
    this.EXT    = this.textureManifest.ext


    this.textures = {}
    this.textureLoadPromise = null
    this.loadState = {
      loaded: 0,
      total: this.textureManifest.items.length,
      progress: 0,
      currentKey: null,
      isComplete: false,
    }


    this.onResize = this.resize.bind(this)

    this.init().catch((error) => {
      console.error('[kv] Pixi init failed:', error)
    })
  }

  handleVisibilityChange() {
    this.isPageVisible = !document.hidden
    this.updateRenderingState()
  }

  startPixiRendering() {
    if(!this.app || this.isRendering) return

    if(typeof this.app.start === 'function') {
      this.app.start()
    }
    else if(this.app.ticker && typeof this.app.ticker.start === 'function') {
      this.app.ticker.start()
    }

    this.isRendering = true
  }

  stopPixiRendering() {
    if(!this.app || !this.isRendering) return

    if(typeof this.app.stop === 'function') {
      this.app.stop()
    }
    else if(this.app.ticker && typeof this.app.ticker.stop === 'function') {
      this.app.ticker.stop()
    }

    this.isRendering = false
  }

  updateRenderingState() {
    if(!this.app) return

    if(this.isPageVisible && this.isInViewport) {
      this.startPixiRendering()
      return
    }

    this.stopPixiRendering()
  }

  setupRenderingControl() {
    this.isRendering = true

    if(typeof document !== 'undefined' && document.addEventListener) {
      document.addEventListener('visibilitychange', this.onVisibilityChange)
    }

    if(typeof IntersectionObserver === 'function') {
      this.viewportObserver = new IntersectionObserver((entries) => {
        const entry = entries[0]
        this.isInViewport = Boolean(entry && entry.isIntersecting)
        this.updateRenderingState()
      }, {
        threshold: 0,
      })

      this.viewportObserver.observe(this.rootEl)
      return
    }

    this.isInViewport = true
    this.updateRenderingState()
  }

  getStageSize() {
    const width  = this.stageEl.clientWidth
    const height = this.stageEl.clientHeight
    return { width, height }
  }

  updateLoadProgress(loaded, currentKey, isComplete = false) {
    const total = this.textureManifest.items.length || 1
    const progress = loaded / total

    this.loadState = {
      loaded,
      total,
      progress,
      currentKey,
      isComplete,
    }

    // console.log(this.loadState)
    this.rootEl.setAttribute('data-kv-load-progress', String(progress))
    this.rootEl.style.setProperty('--kv-load-progress', String(progress))

    // if(typeof this.onLoadProgress === 'function') {
    //   this.onLoadProgress(this.getLoadProgress())
    // }
  }

  // getLoadProgress() {
  //   return Object.assign({}, this.loadState)
  // }

  loadTextures() {
    if(this.textureLoadPromise) return this.textureLoadPromise

    let loadedCount = 0
    this.updateLoadProgress(0, null, false)

    this.textureLoadPromise = Promise.all(this.textureManifest.items.map(async (asset) => {
      const texture = await this.Assets.load(`${this.PATH + asset.key + "_" + this.DEVICE + this.EXT}`)
      loadedCount += 1
      this.updateLoadProgress(loadedCount, asset.key, loadedCount === this.textureManifest.items.length)
      return [asset.key, texture]
    })).then((entries) => {
      this.textures = entries.reduce((acc, entry) => {
        const key = entry[0]
        const texture = entry[1]
        acc[key] = texture
        return acc
      }, {})

      this.updateLoadProgress(this.textureManifest.items.length, 'complete', true)

      return this.textures
    })

    return this.textureLoadPromise
  }

  getTexture(key) {
    if(!this.textures[key]) {
      throw new Error(`[kv] texture not found: ${key}`)
    }

    return this.textures[key]
  }

  createItem(texture, asset, index)
  {
    const container = new this.Container()
    this.rootScene.addChild(container)
    container.label = asset.key
    container.zIndex = index


    const imageWrapper = new this.Container()
    container.addChild(imageWrapper)

    const image = new this.Sprite(texture)
    imageWrapper.addChild(image)

    image.width  = texture.width * .5
    image.height = texture.height * .5


    const mask = new this.Graphics()
    container.addChild(mask)

    mask.clear()
    mask.beginFill(0xcccccc, 1)
    mask.moveTo(-this.MASK_WIDTH * .5, -this.MASK_HEIGHT * .5)
    mask.lineTo(this.MASK_WIDTH * .5, -this.MASK_HEIGHT * .5)
    mask.lineTo(this.MASK_WIDTH * .5, this.MASK_HEIGHT * .5)
    mask.lineTo(-this.MASK_WIDTH * .5, this.MASK_HEIGHT * .5)
    mask.closePath()
    mask.endFill()

    mask.skew.x = this.MASK_SKEW_X

    if(asset.type === "R")
    {
      mask.pivot.set(-this.MASK_WIDTH * .5, 0)
    }
    else
    {
      mask.pivot.set(this.MASK_WIDTH * .5, 0)
    }

    image.mask = mask



    if(asset.type === "R")
    {
      // 下からのマスク用
      const imageMask = new this.Graphics()
      container.addChild(imageMask)


      const maskRectW  = this.MASK_WIDTH * 2
      const maskRectH  = this.MASK_HEIGHT * 2

      imageMask.clear()
      imageMask.beginFill(0xcccccc, 1)
      imageMask.moveTo(0, 0)
      imageMask.lineTo(maskRectW, 0)
      imageMask.lineTo(maskRectW, maskRectH)
      imageMask.lineTo(0, maskRectH)
      imageMask.closePath()
      imageMask.endFill()

      container.__imageMask = imageMask
    }

    container.__imageWrapper = imageWrapper

    container.__key     = asset.key
    container.__type    = asset.type
    container.__index   = index
    container.__image   = image
    container.__mask    = mask
    container.__texture = texture
    container.__asset   = asset



    container.visible = false

    container.scale.set(this.CONTAINER_INIT_SCALE)

    // if(asset.type === "L")
    // {
    //   container.zIndex = 10;
    // }

    mask.scale.set(this.rootScale)

    const imgScale = this.rootScale * asset[this.DEVICE].smallScale
    image.scale.set(imgScale)
    image.x = this.rootScale * asset[this.DEVICE].smallX
    image.y = this.rootScale * asset[this.DEVICE].smallY

    image.anchor.set(.5, .5)

    this[asset.key] = container

    return { key : asset.key, index, container, image, mask }
  }

  createItems() {
    this.items = this.textureManifest.items.map((asset, index) => {
      const texture = this.getTexture(asset.key)
      return this.createItem(texture, asset, index)
    })

    return this.items
  }

  createFrontCross()
  {
    this.frontCrossContainer = new this.Container()
    this.rootScene.addChild(this.frontCrossContainer)
    this.frontCrossContainer.zIndex = 20


    // --------------

    this.frontCrossWrapper1 = new this.Container()
    this.frontCrossContainer.addChild(this.frontCrossWrapper1)

    this.frontCross1 = new this.Graphics()
    this.frontCrossWrapper1.addChild(this.frontCross1)
    this.frontCross1.visible = false

    const gradientFill1 = new this.PIXI.FillGradient(0, 0, 0, 1)
    gradientFill1.addColorStop(0, 'rgba(187, 226, 241, 0)');
    gradientFill1.addColorStop(1, 'rgba(187, 226, 241, 0.2)');

    this.frontCross1.clear()
    this.frontCross1.moveTo(0, -this.CROSS_HEIGHT * .5)
    this.frontCross1.lineTo(this.CROSS_WIDTH, -this.CROSS_HEIGHT * .5)
    this.frontCross1.lineTo(this.CROSS_WIDTH, this.CROSS_HEIGHT * .5)
    this.frontCross1.lineTo(0, this.CROSS_HEIGHT * .5)
    this.frontCross1.closePath()
    this.frontCross1.fill(gradientFill1);
    this.frontCross1.endFill()

    this.frontCrossWrapper1.skew.x = -this.MASK_SKEW_X
    this.frontCrossWrapper1.pivot.set(this.CROSS_WIDTH * 1, .5)

    // --------------

    this.frontCrossWrapper2 = new this.Container()
    this.frontCrossContainer.addChild(this.frontCrossWrapper2)


    this.frontCross2 = new this.Graphics()
    this.frontCrossWrapper2.addChild(this.frontCross2)
    this.frontCross2.blendMode = 'multiply';
    this.frontCross2.visible = false

    const gradientFill2 = new this.PIXI.FillGradient(0, 0, 0, 1)
    gradientFill2.addColorStop(0,   'rgba(187, 226, 241, 0.0)');
    gradientFill2.addColorStop(0.1, 'rgba(187, 226, 241, 1.0)');

    this.frontCross2.clear()
    // this.frontCross2.beginFill(0xBBE2F1)
    this.frontCross2.moveTo(0, -this.CROSS_HEIGHT * .6)
    this.frontCross2.lineTo(this.CROSS_WIDTH, -this.CROSS_HEIGHT * .6)
    this.frontCross2.lineTo(this.CROSS_WIDTH, this.CROSS_HEIGHT * .5)
    this.frontCross2.lineTo(0, this.CROSS_HEIGHT * .5)
    this.frontCross2.closePath()
    this.frontCross2.fill(gradientFill2);
    this.frontCross2.endFill()

    this.frontCrossWrapper2.skew.x = this.MASK_SKEW_X
    this.frontCross2.pivot.set(this.CROSS_WIDTH * 1, .5)

    // --------------

    this.frontCrossWrapper3 = new this.Container()
    this.frontCrossContainer.addChild(this.frontCrossWrapper3)

    this.frontCross3 = new this.Graphics()
    this.frontCrossWrapper3.addChild(this.frontCross3)
    this.frontCross3.visible = false

    const gradientFill3 = new this.PIXI.FillGradient(0, 0, 0, 1)
    gradientFill3.addColorStop(.2, 'rgba(187, 226, 241, 0.8)');
    gradientFill3.addColorStop(1, 'rgba(187, 226, 241, 0.0)');

    this.frontCross3.clear()
    this.frontCross3.moveTo(0, -this.CROSS_HEIGHT * .5)
    this.frontCross3.lineTo(this.CROSS_WIDTH, -this.CROSS_HEIGHT * .5)
    this.frontCross3.lineTo(this.CROSS_WIDTH, this.CROSS_HEIGHT * .5)
    this.frontCross3.lineTo(0, this.CROSS_HEIGHT * .5)
    this.frontCross3.closePath()
    this.frontCross3.fill(gradientFill3);
    this.frontCross3.endFill()

    this.frontCrossWrapper3.skew.x = -this.MASK_SKEW_X
    this.frontCross3.pivot.set(this.CROSS_WIDTH * 0, .5)

    // --------------

    this.frontCrossWrapper4 = new this.Container()
    this.frontCrossContainer.addChild(this.frontCrossWrapper4)

    this.frontCross4 = new this.Graphics()
    this.frontCrossWrapper4.addChild(this.frontCross4)
    this.frontCross4.blendMode = 'multiply';
    this.frontCross4.visible = false

    const gradientFill4 = new this.PIXI.FillGradient(0, 0, 0, 1)
    gradientFill4.addColorStop(.4, 'rgba(230, 0, 45, 0.9)');
    gradientFill4.addColorStop(1, 'rgba(230, 0, 45, 0.0)');

    this.frontCross4.clear()
    this.frontCross4.moveTo(0, -this.CROSS_HEIGHT * .5)
    this.frontCross4.lineTo(this.CROSS_WIDTH, -this.CROSS_HEIGHT * .5)
    this.frontCross4.lineTo(this.CROSS_WIDTH, this.CROSS_HEIGHT * .5)
    this.frontCross4.lineTo(0, this.CROSS_HEIGHT * .5)
    this.frontCross4.closePath()
    this.frontCross4.fill(gradientFill4);
    this.frontCross4.endFill()

    this.frontCrossWrapper4.skew.x = this.MASK_SKEW_X
    this.frontCross4.pivot.set(this.CROSS_WIDTH * 0, .5)


    // gsap.fromTo(this.frontCross4, {y:-1500},  { duration: 1, ease: "power2.out", y:0, yoyo:true, repeat:-1 })

    // --------------

    this.frontCrossWrapper5 = new this.Container()
    this.rootScene.addChild(this.frontCrossWrapper5)

    this.frontCross5 = new this.Graphics()
    // this.frontCrossContainer.addChild(this.frontCross5)
    this.frontCrossWrapper5.addChild(this.frontCross5)
    this.frontCrossWrapper5.zIndex = 20
    this.frontCross5.visible = false

    const gradientFill5 = new this.PIXI.FillGradient(0, 0, 0, 1)
    gradientFill5.addColorStop(.9, 'rgba(187, 226, 241, 0.3)');
    gradientFill5.addColorStop(1, 'rgba(187, 226, 241, 0.0)');

    this.frontCross5.clear()
    // this.frontCross5.beginFill('rgba(187, 226, 241, 0.3)')
    this.frontCross5.moveTo(-this.CROSS_WIDTH, -this.CROSS_HEIGHT)
    this.frontCross5.lineTo(0, -this.CROSS_HEIGHT)
    this.frontCross5.lineTo(0, this.CROSS_HEIGHT * .1)
    this.frontCross5.lineTo(-this.CROSS_WIDTH, this.CROSS_HEIGHT * .1)
    this.frontCross5.closePath()
    this.frontCross5.fill(gradientFill5);
    this.frontCross5.endFill()

    this.frontCrossWrapper5.skew.x = this.MASK_SKEW_X
    this.frontCross5.pivot.set(0, 0)
  }




  ////////////////////////////////////////////////////////////

  async init() {
    const { width, height } = this.getStageSize()

    const app = new this.Application()
    this.app = app

    if(typeof app.init === 'function') {
      await app.init({
        width,
        height,
        backgroundAlpha: 0,
        antialias: true,
        resolution: 2,
        autoDensity: true
      })
    }

    this.stageEl.appendChild(this.app.canvas || this.app.view)

    this.rootScene = new this.Container()
    this.rootScene.sortableChildren = true
    this.app.stage.addChild(this.rootScene)

    await this.loadTextures()

    this.createItems()
    this.createFrontCross()

    window.addEventListener('resize', this.onResize)
    this.setupRenderingControl()

    this.resize()
    this.start()
  }

  ////////////////////////////////////////////////////////////

  start()
  {
    let wait = .2

    delayedCall(() =>
    {
      this.title.classList.add("is-show")
    }, wait)

    // return
    wait += isSP() ? 3.25 : 2.8

    delayedCall(() =>
    {
      this.title.classList.add("is-fixed")
      this.home.dataset.status = "ready"
      this.showCross()
    }, wait)
  }


  showCross()
  {
    let wait = 0

    delayedCall(() =>
    {
      this.back_cross.classList.add("is-show")
    }, wait)

    wait += .8

    delayedCall(() =>
    {
      this.back_cross.classList.add("move-right")
    }, wait)

    wait += isSP() ? 0 : .2

    delayedCall(() =>
    {
      this.slideInFirst()
    }, wait)
  }


  ////////////////////////////////////////////////////////////

  // 右から中央へスライドイン
  slideInFirst()
  {
    const itemL = this.img4a
    const itemC = this.img1a
    const itemR = this.img2a

    const dur = 1.2

    itemL.visible = true
    itemC.visible = true
    itemR.visible = true

    let   marginX   = isSP() ? vw(254) : Math.max(Math.min(this.stageW * .3, 700), 240 * this.rootScale)
    const maskWidth = this.MASK_WIDTH * this.rootScale

    let startX_L = this.stageW
    let startX_C = this.stageW + marginX * 1
    let startX_R = this.stageW + marginX * 2

    let endX_L  = maskWidth * .5 * this.CONTAINER_INIT_SCALE - marginX
    let endX_C  = maskWidth * .5 * this.CONTAINER_INIT_SCALE
    let endX_R  = maskWidth * .5 * this.CONTAINER_INIT_SCALE + marginX

    if(this.isTabletPortrait())
    {
      marginX  = this.stageW * .35
      startX_C = this.stageW + marginX * 1.4
      startX_R = this.stageW + marginX * 2.8

      endX_L  = maskWidth * .5 * this.CONTAINER_INIT_SCALE - marginX * 1.4
      endX_R  = maskWidth * .5 * this.CONTAINER_INIT_SCALE + marginX * 1.4
    }


    //------------------
    // リサイズされたときのためにここでリセットする
    const setDefaultSize = (target) => {

      const maskWidth  = this.MASK_WIDTH * this.rootScale
      const maskHeight = this.MASK_HEIGHT * this.rootScale

      const imgScale = this.rootScale * target.__asset[this.DEVICE].smallScale
      const img_X    = this.rootScale * target.__asset[this.DEVICE].smallX
      const img_Y    = this.rootScale * target.__asset[this.DEVICE].smallY

      gsap.set(target.__mask, { width:maskWidth, height:maskHeight })
      gsap.set(target.__image.scale, { x:imgScale, y:imgScale })
      gsap.set(target.__image.position, { x:img_X, y:img_Y })
    }

    setDefaultSize(this.img1a)
    setDefaultSize(this.img1b)
    setDefaultSize(this.img2a)
    setDefaultSize(this.img2b)
    setDefaultSize(this.img3a)
    setDefaultSize(this.img3b)
    setDefaultSize(this.img4a)
    setDefaultSize(this.img4b)
    //--------------------

    gsap.fromTo(itemL.position, { x:startX_L }, { duration: dur, ease: "expo.out", x:endX_L })
    gsap.fromTo(itemC.position, { x:startX_C }, { duration: dur, ease: "expo.out", x:endX_C })
    gsap.fromTo(itemR.position, { x:startX_R }, { duration: dur, ease: "expo.out", x:endX_R })

    // this.img1a.visible = true
    // gsap.fromTo(this.img1a.position, { x:startX_R }, { duration: dur, ease: "expo.out", x:endX_C })
    // this.img2a.visible = true
    // gsap.fromTo(this.img2a.position, { x:startX_R }, { duration: dur, ease: "expo.out", x:endX_C })
    // this.img2a.zIndex = 20
    // this.img3a.visible = true
    // gsap.fromTo(this.img3a.position, { x:startX_R }, { duration: dur, ease: "expo.out", x:endX_C })
    // this.img3a.zIndex = 20
    // this.img4a.visible = true
    // gsap.fromTo(this.img4a.position, { x:startX_R }, { duration: dur, ease: "expo.out", x:endX_C })
    // this.img4a.zIndex = 20
    // this.img1b.visible = true
    // gsap.fromTo(this.img1b.position, { x:startX_R }, { duration: dur, ease: "expo.out", x:endX_C - 150 })
    // this.img1b.__imageMask.visible = false
    // this.img2b.visible = true
    // gsap.fromTo(this.img2b.position, { x:startX_R }, { duration: dur, ease: "expo.out", x:endX_C - 150 })
    // this.img2b.__imageMask.visible = false
    // this.img3b.visible = true
    // gsap.fromTo(this.img3b.position, { x:startX_R }, { duration: dur, ease: "expo.out", x:endX_C - 150 })
    // this.img3b.__imageMask.visible = false
    // this.img4b.visible = true
    // this.img4b.__imageMask.visible = false
    // gsap.fromTo(this.img4b.position, { x:startX_R }, { duration: dur, ease: "expo.out", x:endX_C - 150 })

    // return

    let wait = dur + 0

    delayedCall(() =>
    {
      this.motionShowLR()
    }, wait)
  }

  // 中央から左右に分かれつつRが出てくる
  motionShowLR()
  {
    this.img1a.zIndex =
    this.img1b.zIndex =
    this.img2a.zIndex =
    this.img2b.zIndex =
    this.img3a.zIndex =
    this.img3b.zIndex =
    this.img4a.zIndex =
    this.img4b.zIndex = 1

    const {itemL, itemR} = this.getCurrentItems(this.animationCount)

    itemL.zIndex = 20
    itemR.zIndex = 10


    const dur1 = .5
    let wait = 0

    const ease = "power1.inOut"

    itemR.visible = true


    const maskWidth = this.MASK_WIDTH * this.rootScale

    const img_endX    = isSP() ? 24 * this.rootScale : 30 * this.rootScale
    const imgR_startX = maskWidth * .5 * this.CONTAINER_INIT_SCALE


    // 左右に分かれて出る

    const itemRScale = isSP() ? this.CONTAINER_INIT_SCALE : this.CONTAINER_INIT_SCALE
    const endScale   = isSP() ? 1 : 1

    gsap.to(itemL.scale, { duration: dur1, ease: ease, x:endScale, y:endScale })
    gsap.fromTo(itemR.scale, {x:itemRScale, y:itemRScale}, { duration: dur1, ease: ease, x:endScale, y:endScale })

    gsap.to(itemL.position, { duration: dur1, ease: ease, x:-img_endX })
    gsap.fromTo(itemR.position, {x:-imgR_startX}, { duration: dur1, ease: ease, x:img_endX })

    // 右の下からマスク
    const imageRMaskX  = itemR.__mask.width * -.5
    const imageRMaskY1 = itemR.__mask.height * .5
    const imageRMaskY2 = itemR.__mask.height * .5
    const imageRMaskW  = itemR.__mask.width * 2
    const imageRMaskH  = itemR.__mask.height * 1

    itemR.__imageMask.width = imageRMaskW
    itemR.__imageMask.height = imageRMaskH

    itemR.__imageWrapper.mask = itemR.__imageMask
    itemR.__imageMask.visible = true

    gsap.fromTo(itemR.__imageMask.position, {x:imageRMaskX, y:imageRMaskY1}, { duration: dur1 * .7, delay: dur1 * .2, ease: "power2.out", y:-imageRMaskY2 })

    // return

    const dur2 = .3
    wait += dur1 * 1

    gsap.to(itemL.position, { duration: dur2, delay:wait, ease: "power1.out", x:0 })
    gsap.to(itemR.position, { duration: dur2, delay:wait, ease: "power1.out", x:0, onStart:()=>
      {
        itemR.__imageWrapper.mask = null
        itemR.__imageMask.visible = false
      } })

    // return

    wait += dur2

    delayedCall(() =>
    {
      this.motionScaleUp()
    }, wait)
  }



  // 中央横並びから全画面に拡大
  motionScaleUp()
  {
    const {itemL, itemR} = this.getCurrentItems(this.animationCount)

    const dur = 1

    const maskScaleW = 1.4
    // const maskScaleH = 1.0
    const maskScaleH = 1.051

    // const ease = "power4.inOut"
    const ease = CustomEase.create("custom", "M0,0 C0.189,0 0.195,0.016 0.267,0.101 0.336,0.184 0.368,0.36 0.4,0.5 0.42,0.59 0.453,0.768 0.516,0.862 0.591,0.973 0.704,1 1,1 ")


    let maskWidth, maskHeight, twScaleL, twScaleR, imageLX, imageRX

    if(isSP())
    {
      maskWidth  = this.stageW * .75 * maskScaleW
      maskHeight = this.stageH * maskScaleH

      twScaleL = this.stageH / (itemL.__texture.height * .5) * .5
      twScaleR = this.stageH / (itemR.__texture.height * .5) * .5

      imageLX = this.stageW * itemL.__asset[this.DEVICE].largeX
      imageRX = this.stageW * itemR.__asset[this.DEVICE].largeX
    }
    else if(this.isTabletPortrait())
    {
      maskWidth  = this.stageW * .5 * maskScaleW * 1.1
      maskHeight = this.stageH * maskScaleH

      twScaleL = Math.max(this.stageW / (itemL.__texture.width * .5) * .426, this.stageH / (itemL.__texture.height * .5) * .5)
      twScaleR = Math.max(this.stageW / (itemR.__texture.width * .5) * .426, this.stageH / (itemR.__texture.height * .5) * .5)

      // twScaleL = this.stageW / (itemL.__texture.width * .5) * .426
      imageLX = this.stageW * itemL.__asset[this.DEVICE].largeTabletX
      imageRX = this.stageW * itemR.__asset[this.DEVICE].largeTabletX
    }
    else
    {
      maskWidth  = this.stageW * .5 * maskScaleW
      maskHeight = this.stageH * maskScaleH

      twScaleL = Math.max(this.stageW / (itemL.__texture.width * .5) * .426, this.stageH / (itemL.__texture.height * .5) * .5)
      twScaleR = Math.max(this.stageW / (itemR.__texture.width * .5) * .426, this.stageH / (itemR.__texture.height * .5) * .5)

      // twScaleL = this.stageW / (itemL.__texture.width * .5) * .426
      imageLX = this.stageW * itemL.__asset[this.DEVICE].largeX
      imageRX = this.stageW * itemR.__asset[this.DEVICE].largeX
    }

    // L
    gsap.to(itemL.__mask, { duration: dur, ease: ease, width:maskWidth, height:maskHeight })

    gsap.to(itemL.__image.scale, { duration: dur, ease: ease, x:twScaleL, y:twScaleL })
    gsap.to(itemL.__image.position, { duration: dur, ease: ease, x:-imageLX, y:0 })


    // R
    gsap.to(itemR.__mask, { duration: dur, ease: ease, width:maskWidth, height:maskHeight })

    gsap.to(itemR.__image.scale, { duration: dur, ease: ease, x:twScaleR, y:twScaleR })
    gsap.to(itemR.__image.position, { duration: dur, ease: ease, x:imageRX, y:0 })


    let wait = dur * .6
    const dur2 = .9

    // this.frontCross1.visible = true
    // this.frontCross2.visible = true
    // this.frontCross3.visible = true
    // this.frontCross4.visible = true
    // if(!isSP()) this.frontCross5.visible = true

    const startY = isSP() ? this.stageH * 1.5 : this.stageH

    // 中央のDCross表示
    const delay2 = .1
    gsap.fromTo(this.frontCross1, {y:startY},  { duration: dur2, delay: wait + delay2 * 3, ease: "power4.out", y:0, visible:true })
    gsap.fromTo(this.frontCross2, {y:startY},  { duration: dur2, delay: wait + delay2 * 2, ease: "power4.out", y:0, visible:true })
    gsap.fromTo(this.frontCross3, {y:-startY}, { duration: dur2, delay: wait + delay2 * 1, ease: "power4.out", y:0, visible:true })
    gsap.fromTo(this.frontCross4, {y:-startY}, { duration: dur2, delay: wait + delay2 * 0, ease: "power4.out", y:0, visible:true })
    if(!isSP() && !this.isTabletPortrait()) gsap.fromTo(this.frontCross5, {y:-startY}, { duration: dur2, delay: wait + delay2 * 3, ease: "power4.out", y:0, visible:true })


    // return

    wait += dur2 + 2.1

    delayedCall(() =>
    {
      this.motionScaleDown1()
    }, wait)
  }

  // 全画面から中央にLRが揃う（少し大きいサイズ）
  motionScaleDown1()
  {
    const {itemL, itemR} = this.getCurrentItems(this.animationCount)

    let wait = 0
    const dur1 = 1.0


    const endY = isSP() ? this.stageH * 2 : this.stageH
    // 中央のDCross非表示
    const delay2 = .06
    gsap.to(this.frontCross1, { duration: dur1, delay: wait + delay2 * 3, ease: "power1.out", y:endY, onComplete:()=>{
      this.frontCross1.visible = false
    } })
    gsap.to(this.frontCross2, { duration: dur1, delay: wait + delay2 * 2, ease: "power1.out", y:endY * 1.1, onComplete:()=>{
      this.frontCross2.visible = false
    } })
    gsap.to(this.frontCross3, { duration: dur1, delay: wait + delay2 * 1, ease: "power1.out", y:-endY, onComplete:()=>{
      this.frontCross3.visible = false
    } })
    gsap.to(this.frontCross4, { duration: dur1, delay: wait + delay2 * 0, ease: "power1.out", y:-endY, onComplete:()=>{
      this.frontCross4.visible = false
    } })
    if(!isSP()) {
      gsap.to(this.frontCross5, { duration: dur1, delay: wait + delay2 * 4, ease: "power1.out", y:-endY, onComplete:()=>{
        this.frontCross5.visible = false
      } })
    }


    wait += .5

    const dur2 = .7

    const ease = "power3.inOut"

    const maskWidth  = this.MASK_WIDTH * this.rootScale
    const maskHeight = this.MASK_HEIGHT * this.rootScale

    const imgScale = this.rootScale * itemL.__asset[this.DEVICE].smallScale
    const imgL_X = this.rootScale * itemL.__asset[this.DEVICE].smallX
    const imgL_Y = this.rootScale * itemL.__asset[this.DEVICE].smallY
    const imgR_X = this.rootScale * itemR.__asset[this.DEVICE].smallX
    const imgR_Y = this.rootScale * itemR.__asset[this.DEVICE].smallY

    const containerX = maskWidth * .5

    // container
    gsap.to(itemL.position, { duration: dur2, delay: wait, ease: ease, x:containerX })
    gsap.to(itemR.position, { duration: dur2, delay: wait, ease: ease, x:-containerX })



    // L
    gsap.to(itemL.__mask, { duration: dur2, delay: wait, ease: ease, width:maskWidth, height:maskHeight })

    gsap.to(itemL.__image.scale, { duration: dur2, delay: wait, ease: ease, x:imgScale, y:imgScale })
    gsap.to(itemL.__image.position, { duration: dur2, delay: wait, ease: ease, x:imgL_X, y:imgL_Y })


    // R
    gsap.to(itemR.__mask, { duration: dur2, delay: wait, ease: ease, width:maskWidth, height:maskHeight })

    gsap.to(itemR.__image.scale, { duration: dur2, delay: wait, ease: ease, x:imgScale, y:imgScale })
    gsap.to(itemR.__image.position, { duration: dur2, delay: wait, ease: ease, x:imgR_X, y:imgR_Y })

    wait += dur2 * .9

    delayedCall(() =>
    {
      this.motionScaleDown2()
    }, wait)
  }

  // 中央にLRが揃った後もとの大きさに戻る
  motionScaleDown2()
  {
    const {itemL, itemR} = this.getCurrentItems(this.animationCount)

    const dur = .4

    const ease = "power1.inOut"

    const maskWidth  = this.MASK_WIDTH * this.rootScale
    const containerX = maskWidth * .5 * this.CONTAINER_INIT_SCALE

    itemR.visible = false

    // container
    gsap.to(itemL.scale, { duration: dur, ease: ease, x:this.CONTAINER_INIT_SCALE, y:this.CONTAINER_INIT_SCALE })
    gsap.to(itemL.position, { duration: dur, ease: ease, x:containerX })

    let wait = dur

    delayedCall(() =>
    {
      this.motionNextItem()
    }, wait)
  }

  // 次の画像へスライド
  motionNextItem()
  {
    let itemL, itemC, itemR1, itemR2

    const count = this.animationCount % 4

    if(count === 0)
    {
      itemL  = this.img4a
      itemC  = this.img1a
      itemR1 = this.img2a
      itemR2 = this.img3a
    }
    else if(count === 1)
    {
      itemL  = this.img1a
      itemC  = this.img2a
      itemR1 = this.img3a
      itemR2 = this.img4a
    }
    else if(count === 2)
    {
      itemL  = this.img2a
      itemC  = this.img3a
      itemR1 = this.img4a
      itemR2 = this.img1a
    }
    else if(count === 3)
    {
      itemL  = this.img3a
      itemC  = this.img4a
      itemR1 = this.img1a
      itemR2 = this.img2a
    }

    if(count === 0 || count === 2)
    {
      this.back_cross.classList.remove("move-right")
      this.back_cross.classList.add("move-left")
    }
    else if(count === 1 || count === 3)
    {
      this.back_cross.classList.remove("move-left")
      this.back_cross.classList.add("move-right")
    }


    const dur = 1
    const ease = "power4.inOut"

    itemL.visible  = true
    itemC.visible  = true
    itemR1.visible = true
    itemR2.visible = true


    const maskWidth  = this.MASK_WIDTH * this.rootScale
    const maskHeight = this.MASK_HEIGHT * this.rootScale
    // const marginX   = isSP() ? vw(254) : this.stageW * .3
    let   marginX = isSP() ? vw(254) : Math.max(Math.min(this.stageW * .3, 700), 240 * this.rootScale)
    const centerX = maskWidth * .5 * this.CONTAINER_INIT_SCALE


    // const startX_L  = centerX - marginX
    const startX_C  = centerX
    // const startX_R1 = centerX + marginX
    const startX_R2 = isSP() ? vw(254 * 3) : this.stageW

    let endX_L  = -this.stageW
    let endX_C  = centerX - marginX
    let endX_R1 = centerX
    let endX_R2 = centerX + marginX


    if(this.isTabletPortrait())
    {
      marginX  = this.stageW * .35
      endX_C  = centerX - marginX * 1.4
      endX_R2 = centerX + marginX * 1.4
    }


    //------------------
    // リサイズされたときのためにここでリセットする
    const setDefaultSize = (target) => {

      const imgScale = this.rootScale * target.__asset[this.DEVICE].smallScale
      const img_X    = this.rootScale * target.__asset[this.DEVICE].smallX
      const img_Y    = this.rootScale * target.__asset[this.DEVICE].smallY

      gsap.to(target.__mask, { duration: dur, ease: ease, width:maskWidth, height:maskHeight })
      gsap.to(target.__image.scale, { duration: dur, ease: ease, x:imgScale, y:imgScale })
      gsap.to(target.__image.position, { duration: dur, ease: ease, x:img_X, y:img_Y })
    }

    setDefaultSize(this.img1a)
    setDefaultSize(this.img1b)
    setDefaultSize(this.img2a)
    setDefaultSize(this.img2b)
    setDefaultSize(this.img3a)
    setDefaultSize(this.img3b)
    setDefaultSize(this.img4a)
    setDefaultSize(this.img4b)
    //--------------------

    // gsap.fromTo(itemL.position, { x:startX_L },   { duration: dur, ease: ease, x:endX_L })
    gsap.to(itemL.position, { duration: dur, ease: ease, x:endX_L })
    gsap.fromTo(itemC.position, { x:startX_C },   { duration: dur, ease: ease, x:endX_C })
    // gsap.fromTo(itemR1.position, { x:startX_R1 }, { duration: dur, ease: ease, x:endX_R1 })
    gsap.to(itemR1.position, { duration: dur, ease: ease, x:endX_R1 })
    gsap.fromTo(itemR2.position, { x:startX_R2 }, { duration: dur, ease: ease, x:endX_R2 })

    let wait = dur

    delayedCall(() =>
    {
      this.animationCount++
      this.motionShowLR()
    }, wait)
  }


  getCurrentItems(count)
  {
    count = count % 4

    let itemL, itemR

    if(count === 0)
    {
      itemL = this.img1a
      itemR = this.img1b
    }
    else if(count === 1)
    {
      itemL = this.img2a
      itemR = this.img2b
    }
    else if(count === 2)
    {
      itemL = this.img3a
      itemR = this.img3b
    }
    else if(count === 3)
    {
      itemL = this.img4a
      itemR = this.img4b
    }

    return { itemL, itemR }
  }

  ////////////////////////////////////////////////////////////

  resize()
  {
    const { width, height } = this.getStageSize()

    this.stageW = width
    this.stageH = height
    // this.rootScale = this.stageH / 800
    this.rootScale  = isSP() ? this.stageH / 800 : this.stageH / 640

    if(this.app.renderer && this.app.renderer.resize) {
      this.app.renderer.resize(width, height)
    }

    this.rootScene.x = width * .5
    this.rootScene.y = height * .5

    if(this.frontCrossContainer)
    {
      const scale = this.rootScale * 1.05
      this.frontCrossContainer.scale.set(scale, scale)

      this.frontCrossWrapper5.scale.set(scale, scale)
      this.frontCrossWrapper5.x = this.stageW * .5
      this.frontCrossWrapper5.y = this.stageH * .5
    }
  }


  isTabletPortrait()
  {
    return document.body.classList.contains("is-tablet-portrait")
  }
}
